import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import RiyalSymbol from '../components/RiyalSymbol';
import { bookingService } from '../services/BookingService';
import { bookingServiceAPI } from '../services/bookingServiceAPI';
import { configService } from '../services/ConfigService';
import { generateAllTicketsPDF } from '../utils/ticketGenerator';
import { paymentService } from '../services/paymentService';
import '../styles/Payment.css';



function Payment() {
  const navigate = useNavigate();
  const paymentFormRef = useRef<HTMLDivElement>(null);
  const [bookingData, setBookingData] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [savedBooking, setSavedBooking] = useState<any>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoMessage, setPromoMessage] = useState('');
  const [isVerifyingPromo, setIsVerifyingPromo] = useState(false);
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'geidea' | 'tamara'>('geidea');
  const [appConfig, setAppConfig] = useState(configService.getConfig());

  useEffect(() => {
    // تحديث الإعدادات في حال تم جلبها متأخراً
    const loadConfig = async () => {
      await configService.reloadConfig();
      setAppConfig(configService.getConfig());
    };
    loadConfig();
  }, []);

  useEffect(() => {
    const data = localStorage.getItem('bookingData');
    if (data) {
      const parsedData = JSON.parse(data);
      setBookingData(parsedData);
      
      // لا نحفظ الحجز محلياً بل ننتظر حتى يضغط المستخدم على زر الدفع أو تجاوز الدفع
    } else {
      navigate('/');
    }
  }, [navigate, savedBooking]);



  if (!bookingData) {
    return <div>جاري التحميل...</div>;
  }

  // رسوم تمارا
  const tamaraFee = paymentMethod === 'tamara' ? 50 : 0;
  
  // حساب المبالغ
  const finalTotalAmount = bookingData.totalAmount - promoDiscount + tamaraFee;
  let finalAmountToPay = finalTotalAmount;
  let depositAmount = 0;
  let depositText = '';
  
  if (appConfig.depositSettings?.enabled && paymentMethod !== 'tamara') {
    const depositSettings = appConfig.depositSettings;
    const isFixed = depositSettings.type === 'fixed';
    depositAmount = isFixed 
      ? depositSettings.amount || 0 
      : Math.round(((bookingData.totalAmount - promoDiscount) * (depositSettings.percentage || 0)) / 100);
      
    finalAmountToPay = depositAmount;
    depositText = isFixed 
      ? 'العربون المطلوب دفعه الآن:' 
      : `العربون المطلوب دفعه الآن (${depositSettings.percentage}%):`;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <motion.div 
      className="payment-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">
            <Link to="/">
              <img src="/images/logo.png" alt="Fotomista" />
            </Link>
          </div>
          <div className="page-title">
            إتمام عملية الدفع
          </div>
        </div>
      </nav>

      <div className="payment-container">
        <div className="booking-summary">
          <h2>ملخص الحجز</h2>
          <div className="summary-details">
            <div className="summary-item">
              <span className="label">نوع الكابينة:</span>
              <span className="value">{bookingData.boothDisplayName || bookingData.boothType}</span>
            </div>
            <div className="summary-item">
              <span className="label">الاسم:</span>
              <span className="value">{bookingData.customerName}</span>
            </div>
            <div className="summary-item">
              <span className="label">رقم الجوال:</span>
              <span className="value">{bookingData.phone}</span>
            </div>
            <div className="summary-item">
              <span className="label">تاريخ المناسبة:</span>
              <span className="value">{formatDate(bookingData.eventDate)}</span>
            </div>
            <div className="summary-item">
              <span className="label">موقع المناسبة:</span>
              <span className="value">{bookingData.eventLocation}</span>
            </div>
            
            {bookingData.bookingType === 'hourly' ? (
              <>
                <div className="summary-item">
                  <span className="label">نظام الحجز:</span>
                  <span className="value">نظام الساعات</span>
                </div>
                <div className="summary-item">
                  <span className="label">عدد الساعات:</span>
                  <span className="value">{bookingData.hours} ساعات</span>
                </div>
                <div className="summary-item">
                  <span className="label">الصور:</span>
                  <span className="value">غير محدودة</span>
                </div>
              </>
            ) : (
              <>
                <div className="summary-item">
                  <span className="label">نظام الحجز:</span>
                  <span className="value">نظام التذاكر</span>
                </div>
                <div className="summary-item">
                  <span className="label">التذاكر المجانية:</span>
                  <span className="value">15 تذكرة</span>
                </div>
                {bookingData.extraTickets > 0 && (
                  <div className="summary-item">
                    <span className="label">تذاكر إضافية:</span>
                    <span className="value">
                      {bookingData.extraTickets} × 35
                      <RiyalSymbol size={16} />
                    </span>
                  </div>
                )}
              </>
            )}
            
            <div className="summary-total">
              <span className="label">المبلغ الأساسي:</span>
              <span className="value">
                {bookingData.totalAmount.toLocaleString('en-US')}
                <RiyalSymbol size={18} />
              </span>
            </div>
            {promoDiscount > 0 && (
              <div className="summary-item promo-discount">
                <span className="label">خصم كود ({appliedPromoCode}):</span>
                <span className="value discount-value">
                  -{promoDiscount.toLocaleString('en-US')}
                  <RiyalSymbol size={16} />
                </span>
              </div>
            )}
            
            {paymentMethod === 'tamara' && (
              <div className="summary-item" style={{ marginTop: '10px', color: '#666' }}>
                <span className="label">رسوم خدمة تمارا:</span>
                <span className="value">
                  {tamaraFee.toLocaleString('en-US')}
                  <RiyalSymbol size={16} />
                </span>
              </div>
            )}
            
            {appConfig.depositSettings?.enabled && paymentMethod !== 'tamara' ? (
                <>
                    <div className="summary-item" style={{ marginTop: '10px', color: '#666' }}>
                      <span className="label">المبلغ الإجمالي النهائي:</span>
                      <span className="value">
                        {finalTotalAmount.toLocaleString('en-US')}
                        <RiyalSymbol size={14} />
                      </span>
                    </div>
                    <div className="summary-total final-total" style={{ borderTop: '2px dashed #D4A574', paddingTop: '15px', marginTop: '10px' }}>
                      <span className="label">{depositText}</span>
                      <span className="value" style={{ color: '#D4A574' }}>
                        {depositAmount.toLocaleString('en-US')}
                        <RiyalSymbol size={18} />
                      </span>
                    </div>
                </>
            ) : (
              <div className="summary-item total" style={{ marginTop: '20px' }}>
                <span className="label">المبلغ الإجمالي:</span>
                <span className="value">
                  {finalTotalAmount.toLocaleString('en-US')}
                  <RiyalSymbol size={20} />
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="payment-form-container">
          <h2>معلومات الدفع</h2>
          {isProcessing ? (
            <div className="payment-loading-container">
              <div className="payment-loading-content">
                <div className="payment-spinner">
                  <div className="spinner-circle"></div>
                  <div className="spinner-circle-2"></div>
                  <div className="spinner-circle-3"></div>
                </div>
                <div className="loading-text">
                  <h3>جاري معالجة الدفع</h3>
                  <p>يرجى عدم إغلاق الصفحة أو الضغط على زر الرجوع</p>
                  <div className="loading-steps">
                    <div className="step active">
                      <div className="step-icon">💳</div>
                      <span>معالجة بيانات الدفع</span>
                    </div>
                    <div className="step">
                      <div className="step-icon">📧</div>
                      <span>إرسال رسالة التأكيد</span>
                    </div>
                    <div className="step">
                      <div className="step-icon">✅</div>
                      <span>إتمام الحجز</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="promo-code-section">
                <h3>كود الخصم</h3>
                <div className="promo-input-group">
                  <input
                    type="text"
                    placeholder="أدخل كود الخصم (اختياري)"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    disabled={isVerifyingPromo || appliedPromoCode !== null}
                  />
                  <button
                    className="verify-promo-btn"
                    onClick={async () => {
                      if (!promoCode.trim()) {
                        setPromoMessage('يرجى إدخال كود الخصم');
                        return;
                      }
                      
                      setIsVerifyingPromo(true);
                      setPromoMessage('');
                      
                      try {
                        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5002'}/api/promo-codes/verify`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            code: promoCode,
                            amount: bookingData.totalAmount
                          })
                        });
                        
                        const data = await response.json();
                        
                        if (data.success) {
                          setPromoDiscount(data.discountAmount);
                          setAppliedPromoCode(data.promoCode.code);
                          setPromoMessage(`✅ تم تطبيق الخصم: ${data.discountAmount} ريال`);
                        } else {
                          setPromoMessage(`❌ ${data.message}`);
                          setPromoDiscount(0);
                          setAppliedPromoCode(null);
                        }
                      } catch (error) {
                        setPromoMessage('❌ خطأ في التحقق من كود الخصم');
                        setPromoDiscount(0);
                        setAppliedPromoCode(null);
                      } finally {
                        setIsVerifyingPromo(false);
                      }
                    }}
                    disabled={isVerifyingPromo || !promoCode.trim() || appliedPromoCode !== null}
                  >
                    {isVerifyingPromo ? 'جاري التحقق...' : appliedPromoCode ? 'تم التطبيق' : 'تطبيق'}
                  </button>
                  {appliedPromoCode && (
                    <button
                      className="remove-promo-btn"
                      onClick={() => {
                        setPromoCode('');
                        setPromoDiscount(0);
                        setAppliedPromoCode(null);
                        setPromoMessage('');
                      }}
                    >
                      إزالة
                    </button>
                  )}
                </div>
                {promoMessage && (
                  <div className={`promo-message ${promoMessage.includes('✅') ? 'success' : 'error'}`}>
                    {promoMessage}
                  </div>
                )}
              </div>
              
              <div className="payment-method-section">
                <h3>طريقة الدفع</h3>
                <div className="payment-method-options">
                  <div
                    className={`payment-method-option ${paymentMethod === 'geidea' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('geidea')}
                  >
                    <div className="payment-option-wrapper">
                      <div className="payment-radio">
                        <div className={`radio-circle geidea ${paymentMethod === 'geidea' ? 'selected' : ''}`}>
                          {paymentMethod === 'geidea' && <div className="radio-dot"></div>}
                        </div>
                      </div>
                      <div className="payment-method-content geidea-content">
                        <div className="geidea-text-wrapper">
                          <span className="payment-method-name">بوابة الدفع الآمنة</span>
                          <span className="payment-method-subtitle">بطاقة مدى فقط</span>
                        </div>
                        <img src="/images/payments.png" alt="وسائل الدفع" className="payment-methods-img" />
                      </div>
                    </div>
                  </div>
                  <div
                    className={`payment-method-option tamara ${paymentMethod === 'tamara' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('tamara')}
                  >
                    <div className="tamara-container">
                      <div className="tamara-radio">
                        <div className={`radio-circle ${paymentMethod === 'tamara' ? 'selected' : ''}`}>
                          {paymentMethod === 'tamara' && <div className="radio-dot"></div>}
                        </div>
                      </div>
                      <div className="tamara-content-wrapper">
                        <div className="tamara-header">
                          <span className="tamara-brand-name">Tamara</span>
                          <img
                            src="/images/tamara-wordmark-ar.svg"
                            alt="تمارا"
                            className="tamara-logo-inline"
                          />
                        </div>
                        <div className="tamara-text">
                          <span className="tamara-main-text">دفعات شهرية. متوافقة مع الشريعة الإسلامية.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="payment-notice">
                <p>{paymentMethod === 'geidea' ? 'سيتم توجيهك إلى بوابة الدفع الآمنة' : 'سيتم توجيهك إلى صفحة تمارا لإتمام عملية الدفع بالتقسيط'}</p>
              </div>
              
              {/* زر الدفع */}
              <button 
                className="pay-now-btn"
                onClick={async () => {
                  setIsProcessing(true);
                  
                  try {
                    // إنشاء الحجز عبر API أولاً قبل إنشاء الدفع
                    let createdBooking = savedBooking;
                    
                    if (bookingData && !savedBooking) {
                      console.log('Original booking data:', bookingData);
                      // تحويل البيانات لتتوافق مع API
                      const apiBookingData: any = {
                        bookingType: bookingData.bookingType,
                        boothType: bookingData.boothType, // القيمة الداخلية للAPI: "classic" أو "modern"
                        boothDisplayName: bookingData.boothDisplayName, // الاسم المعروض من لوحة التحكم
                        customerName: bookingData.customerName,
                        phone: bookingData.phone,
                        email: bookingData.email,
                        eventDate: bookingData.eventDate,
                        eventTime: bookingData.eventTime || '20:00',
                        location: bookingData.eventLocation,
                        totalAmount: finalTotalAmount,
                        baseAmount: bookingData.totalAmount,
                        promoCode: appliedPromoCode,
                        discountAmount: promoDiscount,
                        finalAmount: finalTotalAmount,
                        depositAmount: (appConfig.depositSettings?.enabled && paymentMethod !== 'tamara') ? depositAmount : 0,
                        paidAmount: 0
                      };
                      
                      // إضافة بيانات حسب نوع الحجز
                      if (bookingData.bookingType === 'hourly') {
                        apiBookingData.hours = bookingData.hours;
                      } else if (bookingData.bookingType === 'perPhoto') {
                        // العودة للعدد الأصلي من التذاكر
                        apiBookingData.totalTickets = 15 + (bookingData.extraTickets || 0);
                        apiBookingData.freeTickets = 15;
                        apiBookingData.additionalTickets = bookingData.extraTickets || 0;
                        const config = configService.getConfig();
                        apiBookingData.ticketPrice = config.ticketSystem.ticketPrice;
                        // إضافة تصميم التذكرة
                        if (bookingData.ticketDesign) {
                          apiBookingData.ticketDesign = bookingData.ticketDesign;
                        }
                      }
                      console.log('Creating booking with data:', apiBookingData);
                      createdBooking = await bookingServiceAPI.createBooking(apiBookingData);
                      if (createdBooking) {
                        setSavedBooking(createdBooking);
                      } else {
                        throw new Error('Failed to create booking');
                      }
                    }
                    
                    if (createdBooking) {
                      // Apply promo code if valid
                      if (appliedPromoCode) {
                        try {
                          await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5002'}/api/promo-codes/apply`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ code: appliedPromoCode })
                          });
                        } catch (error) {
                          console.error('Error applying promo code:', error);
                        }
                      }

                      // إنشاء الدفع حسب الطريقة المختارة
                      if (paymentMethod === 'tamara') {
                        // الدفع عبر تمارا
                        console.log('🔄 Starting Tamara payment process...');
                        const tamaraResult = await paymentService.createTamaraCheckout(
                          createdBooking._id || createdBooking.id,
                          {
                            orderReferenceId: createdBooking._id || createdBooking.id,
                            orderNumber: `FOTO-${Date.now()}`,
                            totalAmount: finalAmountToPay,
                            customerName: bookingData.customerName,
                            phone: bookingData.phone,
                            email: bookingData.email,
                            items: [
                              {
                                name: `حجز كابينة ${bookingData.boothDisplayName || bookingData.boothType}`,
                                type: "Digital",
                                reference_id: createdBooking._id || createdBooking.id,
                                sku: `BOOTH-${bookingData.boothType}`,
                                quantity: 1,
                                unit_price: {
                                  amount: finalAmountToPay - tamaraFee,
                                  currency: "SAR"
                                },
                                total_amount: {
                                  amount: finalAmountToPay - tamaraFee,
                                  currency: "SAR"
                                }
                              },
                              ...(tamaraFee > 0 ? [{
                                name: "رسوم خدمة تمارا",
                                type: "Digital",
                                reference_id: `FEE-${createdBooking._id || createdBooking.id}`,
                                sku: "TAMARA-FEE",
                                quantity: 1,
                                unit_price: {
                                  amount: tamaraFee,
                                  currency: "SAR"
                                },
                                total_amount: {
                                  amount: tamaraFee,
                                  currency: "SAR"
                                }
                              }] : [])
                            ]
                          }
                        );

                        if (tamaraResult.success && tamaraResult.checkoutUrl) {
                          // حفظ معلومات الدفع
                          localStorage.setItem('pendingTamaraPayment', JSON.stringify({
                            bookingId: createdBooking._id || createdBooking.id,
                            orderId: tamaraResult.orderId,
                            checkoutId: tamaraResult.checkoutId
                          }));

                          // توجيه المستخدم إلى صفحة تمارا
                          window.location.href = tamaraResult.checkoutUrl;
                        } else {
                          console.error('❌ Failed to create Tamara payment:', tamaraResult.message);
                          alert('فشل في إنشاء عملية الدفع بالتقسيط: ' + tamaraResult.message);
                          setIsProcessing(false);
                          return;
                        }
                      } else {
                        // إنشاء الدفع عبر Geidea
                        console.log('🔄 Starting Geidea payment process...');
                        const geideaResult = await paymentService.createGeideaPayment(
                          createdBooking._id || createdBooking.id,
                          bookingData.email,
                          bookingData.phone
                        );

                        if (geideaResult.success && geideaResult.paymentUrl) {
                          console.log('🔄 Redirecting to Geidea payment page...');
                          window.location.href = geideaResult.paymentUrl;
                          return;
                        } else {
                          console.error('❌ Failed to create Geidea payment:', geideaResult.message);
                          alert('فشل في إنشاء عملية الدفع: ' + geideaResult.message);
                          setIsProcessing(false);
                          return;
                        }
                      }
                    }

                    // الكود القديم - لن يتم تنفيذه
                    /*
                          const paymentUpdateResponse = await fetch(`${process.env.REACT_APP_API_URL}/payment/update-status-test/${createdBooking._id || createdBooking.id}`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              paymentStatus: 'completed',
                              paymentReference: 'TEST-SUCCESS-' + Date.now()
                            })
                          });
                          
                          if (paymentUpdateResponse.ok) {
                            console.log('✅ Payment status updated to completed');
                            
                            // إذا كان نظام التذاكر، قم بإنشاء التذاكر فوراً
                            if (createdBooking.bookingType === 'perPhoto') {
                              try {
                                console.log('🎫 Starting ticket generation immediately after payment confirmation...');
                                const ticketResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/ticket-generation/generate-async/${createdBooking._id || createdBooking.id}`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({
                                    ticketDesign: bookingData.ticketDesign
                                  })
                                });
                                
                                if (ticketResponse.ok) {
                                  const ticketData = await ticketResponse.json();
                                  console.log('✅ Tickets generation started:', ticketData);
                                } else {
                                  const errorData = await ticketResponse.json();
                                  console.error('❌ Failed to start ticket generation:', errorData);
                                }
                              } catch (ticketError) {
                                console.error('❌ Error starting ticket generation:', ticketError);
                              }
                            }
                            
                            // الانتقال لصفحة النجاح
                            navigate(`/payment-success?booking=${createdBooking._id || createdBooking.id}&status=paid`);
                          } else {
                            console.error('❌ Failed to update payment status');
                            navigate(`/payment-success?booking=${createdBooking._id || createdBooking.id}&status=failed`);
                          }
                        } else {
                          // الدفع فشل
                          console.log('❌ Payment failed - updating status');
                          const paymentUpdateResponse = await fetch(`${process.env.REACT_APP_API_URL}/payment/update-status-test/${createdBooking._id || createdBooking.id}`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              paymentStatus: 'failed',
                              paymentReference: 'TEST-FAILED-' + Date.now()
                            })
                          });
                          
                          // الانتقال لصفحة الفشل
                          navigate(`/payment-success?booking=${createdBooking._id || createdBooking.id}&status=failed`);
                        }
                      }
                    } else if (savedBooking) {
                      // الحجز موجود بالفعل
                      if (paymentSuccess) {
                        // الدفع نجح
                        const paymentUpdateResponse = await fetch(`${process.env.REACT_APP_API_URL}/payment/update-status-test/${savedBooking._id || savedBooking.id}`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            paymentStatus: 'completed',
                            paymentReference: 'TEST-SUCCESS-' + Date.now()
                          })
                        });
                        
                        if (paymentUpdateResponse.ok) {
                          // إذا كان نظام التذاكر، قم بإنشاء التذاكر فوراً
                          if (savedBooking.bookingType === 'perPhoto') {
                            try {
                              console.log('🎫 Starting ticket generation for existing booking...');
                              const ticketResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/ticket-generation/generate-async/${savedBooking._id || savedBooking.id}`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json'
                                }
                              });
                              
                              if (ticketResponse.ok) {
                                const ticketData = await ticketResponse.json();
                                console.log('✅ Tickets generation started:', ticketData);
                              } else {
                                const errorData = await ticketResponse.json();
                                console.error('❌ Failed to start ticket generation:', errorData);
                              }
                            } catch (ticketError) {
                              console.error('Error starting ticket generation:', ticketError);
                            }
                          }
                          
                          navigate(`/payment-success?booking=${savedBooking._id || savedBooking.id}&status=paid`);
                        } else {
                          navigate(`/payment-success?booking=${savedBooking._id || savedBooking.id}&status=failed`);
                        }
                      } else {
                        // الدفع فشل
                        await fetch(`${process.env.REACT_APP_API_URL}/payment/update-status-test/${savedBooking._id || savedBooking.id}`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            paymentStatus: 'failed',
                            paymentReference: 'TEST-FAILED-' + Date.now()
                          })
                        });
                        
                        navigate(`/payment-success?booking=${savedBooking._id || savedBooking.id}&status=failed`);
                      }
                    }
                    */
                    
                  } catch (error) {
                    console.error('Error processing payment:', error);
                    alert('حدث خطأ أثناء معالجة الدفع');
                    setIsProcessing(false);
                  }
                }}
              >
                {isProcessing ? 'جاري المعالجة...' : `ادفع الآن - ${finalAmountToPay.toLocaleString('en-US')} ريال`}
              </button>
              
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default Payment;